import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { makeStyles, Tooltip } from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";

import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
} from "@fluentui/react-components";

// Components
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import Spinner from "../../../shared/components/Spinner";
import { TicketHorizontalFilled } from "@fluentui/react-icons";

// Business logic
import { useSelectedTicketsContext } from "../context/SelectedTicketsContext";
import { useQueryClient } from "@tanstack/react-query";
import { useItemQuery } from "../../../shared/hooks/useItemQuery";
import { readTickets } from "../../../features/tickets/services/ticketApi";

const apiUrl = process.env.REACT_APP_API_URL?.trim();
const useStyles = makeStyles({
  container: {
    //height: "calc(100dvh - 200px)", // adjust based on layout
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    height: "100%",
    overflowX: "auto",
    overflowY: "auto",
  },
  dataGrid: {
    height: "100%",
    minWidth: "fit-content",
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
  },
});

const UsersDataGrid = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { value: selectedTickets, setValue: setSelectedTickets } = useSelectedTicketsContext();
  const queryClient = useQueryClient();
  const [errors, setErrors] = React.useState([]);

  // Handle URL search params and pass to Query
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(queryParams.entries());

  const { data = [], isFetching, error } = useItemQuery(["tickets"], () => readTickets(params), true);

  React.useEffect(() => {
    if (error) {
      setErrors([{ message: error?.message || t("generic.error"), type: "error", details: error }]);
    }
  }, [error, t]);

  React.useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
  }, [location.search]);

  if (isFetching) return <Spinner label={t("generic.loading")} />;

  const columns = [
    createTableColumn({
      columnId: "type",
      renderHeaderCell: () => t("generic.type"),
      renderCell: (item) => (
        <TableCellLayout>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <TicketHorizontalFilled
              color={item?.type === "INC" ? "#FFA500" : "#28A745"}
              style={{ width: 24, height: 24 }}
            />
            {item.type}-{item.id}
          </span>
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "status",
      renderHeaderCell: () => t("generic.status"),
      renderCell: (item) => <TableCellLayout>{item.status}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "requested_by",
      renderHeaderCell: () => t("generic.requester"),
      renderCell: (item) => (
        <TableCellLayout>
          {item.requested_by}
          {item.requested_for !== item.requested_by && (
            <span>
              <br /> {t("nounPhrases.forSomeone", { name: item.requested_for })}
            </span>
          )}
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "sn",
      renderHeaderCell: () => t("generic.title"),
      renderCell: (item) => <TableCellLayout style={{ width: "100%" }}>{item.title}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "site",
      renderHeaderCell: () => t("generic.site"),
      renderCell: (item) => <TableCellLayout>{item.site}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "impact",
      renderHeaderCell: () => t("generic.impact"),
      renderCell: (item) => <TableCellLayout>{t(`generic.${item.impact?.toLowerCase()}`)}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "urgency",
      renderHeaderCell: () => t("generic.urgency"),
      renderCell: (item) => <TableCellLayout>{t(`generic.${item.urgency?.toLowerCase()}`)}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "priority",
      renderHeaderCell: () => t("generic.priority"),
      renderCell: (item) => <TableCellLayout>{t(`generic.${item.priority?.toLowerCase()}`)}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "created_at",
      renderHeaderCell: () => t("generic.creationDate"),
      renderCell: (item) => <TableCellLayout>{item.created_at}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "modified_at",
      renderHeaderCell: () => t("generic.modificationDate"),
      renderCell: (item) => <TableCellLayout>{item.modified_at}</TableCellLayout>,
    }),
  ];

  const baseWidth = 100;
  const columnSizingOptions = {
    type: { minWidth: 75 }, // allow shrinking to content
    status: { minWidth: 50 }, // allow shrinking to content
    // ...others if needed...
  };
  const handleSelectionChange = (event, selectionData) => {
    const ids = [...selectionData.selectedItems];
    const newSelection = data.filter((item) => ids.includes(item.id));
    setSelectedTickets(newSelection);
  };

  return (
    <div className={styles.container}>
      <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
      <DataGrid
        items={data}
        columns={columns}
        columnSizingOptions={columnSizingOptions}
        selectedItems={selectedTickets.map((item) => item.id)}
        getRowId={(item) => item.id}
        focusMode="composite"
        resizableColumns
        selectionMode="single"
        onSelectionChange={handleSelectionChange}
        className={styles.dataGrid}
      >
        <DataGridHeader>
          <DataGridRow
            className={styles.stickyHeaderRow}
            selectionCell={{
              checkboxIndicator: { "aria-label": "Select all rows" },
            }}
          >
            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>

        <DataGridBody>
          {({ item, rowId }) => (
            <DataGridRow
              key={rowId}
              selectionCell={{
                checkboxIndicator: { "aria-label": "Select row" },
              }}
            >
              {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );
};

export default UsersDataGrid;
